import z from "zod";
import type { SiteVisitBlock } from "@remi/types/site-visit-availability";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const contractorErrorTypeValues = [
  "FAILED_TO_CREATE_GOOGLE_CALENDAR",
  "REGIONS_NOT_FOUND",
  "NO_PHONE_NUMBER_FOUND_FOR_HOMEOWNER_OR_CONTRACTOR",
  "FAILED_TO_UPDATE_COUNTY_FROM_ADDRESS",
  "PROPERTY_ADDRESS_NOT_FOUND",
  "COULD_NOT_FIND_COUNTY_OR_LOCALITY_FOR_ADDRESS",
  "MARKET_CREATION_FAILED",
  "MARKET_NOT_FOUND_FOR_SERVICE_AREA",
  "COUNTIES_NOT_FOUND",
  "CONTRACTOR_PROFILE_NOT_FOUND",
  "PROJECT_NOT_FOUND",
  "CANNOT_FILTER_CONTRACTORS_WITHOUT_MARKET_ID",
  "CANNOT_FILTER_CONTRACTORS_WITHOUT_WORKSITE_GEO_LOCATION",
  "FAILED_TO_GET_CONTRACTORS_WITH_MARGIN",
  "BID_NOT_FOUND",
  "CPM_NOT_FOUND",
  "PROJECT_HAS_NO_BID",
  "DUPLICATE_CONTRACTOR_NAME",
  "COMPLIANCE_REMINDER_INVALID_REQUEST",
  "CONTRACTOR_USER_PRIMARY_EMAIL_REQUIRED",
  "DOCUMENT_REJECTION_INVALID_RECIPIENTS",
  "SCORECARD_SNAPSHOT_NOT_FOUND",
  "SCORECARD_SNAPSHOT_READBACK_FAILED",
  "SCORECARD_UNKNOWN_METRIC_KEY",
  "SCORECARD_WEIGHT_OUT_OF_RANGE",
  "SERVICE_AREA_NOT_FOUND",
  "FAILED_TO_WRITE_SELECTION_HISTORY",
  "SURVEY_ONLY_CONTRACTOR_ON_NON_SURVEY_PROJECT",
  "PREFERRED_AUTO_ASSIGNMENT_INCOMPLETE",
  "DOCUMENT_EXPIRATION_REMINDER_BATCH_FAILED",
  "COMMITMENT_NOT_FOUND",
  "COMMITMENT_EDIT_CONFLICT",
  "COMMITMENT_DELETE_UNAUTHORIZED",
  "PRE_ASSIGNMENT_NOT_FOUND",
  "ASSIGNEE_NOT_REMI_INTERNAL",
  "ASSIGNEE_NOT_ACTIVE_SITE_INSPECTOR",
  "ASSIGNEE_PERSON_NOT_FOUND",
  "RECEIPT_NOT_FOUND",
  "EXPORT_USER_NOT_CONTRACTOR",
  "EXPORT_DISABLED_FOR_ORG",
  "EXPORT_RATE_LIMITED",
  "SITE_INSPECTOR_NOT_ACTIVE_IN_PROJECT_ORG",
  "SITE_INSPECTOR_NOT_QUALIFIED_FOR_PROJECT_CATEGORY",
  "SITE_INSPECTOR_NOT_IN_CONTRACTOR_ORG",
  "SITE_INSPECTOR_HAS_UPCOMING_VISITS",
  "SITE_INSPECTOR_INVALID_WORKING_HOURS",
  "SITE_INSPECTOR_INVALID_TIME_OFF",
  "NOMAD_JOB_TYPE_ROLES_CONFER_CAPABILITIES",
  "SITE_VISIT_APPOINTMENT_CONFLICT",
  "SITE_VISIT_APPOINTMENT_NOT_IN_CONTRACTOR_ORG",
  "SITE_VISIT_APPOINTMENT_NOT_ON_INSPECTOR_ROUTE",
  "SITE_VISIT_APPOINTMENT_NOT_SCHEDULED",
  "SITE_VISIT_ANOTHER_VISIT_EN_ROUTE",
  "DISPATCH_REMOVAL_REFUSED",
  "INSPECTOR_SCHEDULE_SMS_LINE_NOT_CONFIGURED",
  "CONTRACTOR_NOT_IN_ELIGIBLE_LIST",
  "CONTRACTOR_REASSIGNMENT_BLOCKED",
  "CONTRACTOR_ALREADY_ASSIGNED_TO_PROJECT",
  "PROJECTS_NOT_ON_SAME_BID",
  "CONTRACTOR_CAPABILITY_GROUPING_NOT_FOUND",
  "CONTRACTOR_CAPABILITY_GROUPING_HAS_LINE_ITEMS",
  "CONTRACTOR_CAPABILITY_GROUPING_NAME_TAKEN",
  "NOMAD_AUTO_ASSIGN_COMPENSATION_FAILED",
  "NOMAD_BOOKING_OWNER_COMPENSATION_FAILED",
  "NOMAD_ORG_HAS_NO_TAGGED_INSPECTORS",
  "SIMULATION_MARKET_NOT_FOUND",
  "TRAINING_SANDBOX_REQUIRED",
  "TRAINING_CONTRACTOR_NOT_ACTIVE",
  "TRAINING_SANDBOX_PROVISION_REJECTED",
  "TRAINING_SANDBOX_PROVISION_FAILED",
  "PRACTICE_CATEGORY_NOT_APPROVED",
  "PRACTICE_JOB_SEED_INCOMPLETE",
  "PRACTICE_JOB_NOT_ON_PLATE",
] as const;
const contractorErrorTypeSchema = z.enum(contractorErrorTypeValues);
const ContractorErrorType = contractorErrorTypeSchema.enum;
type ContractorErrorType = z.infer<typeof contractorErrorTypeSchema>;

export type ContractorErrors =
  | { errorType: typeof ContractorErrorType.SIMULATION_MARKET_NOT_FOUND; data: { marketId: number } }
  | { errorType: typeof ContractorErrorType.TRAINING_SANDBOX_REQUIRED; data: Record<string, never> }
  | {
      errorType: typeof ContractorErrorType.TRAINING_CONTRACTOR_NOT_ACTIVE;
      data: { contractorName: string; status: string };
    }
  | { errorType: typeof ContractorErrorType.PRACTICE_CATEGORY_NOT_APPROVED; data: { category: string } }
  | { errorType: typeof ContractorErrorType.PRACTICE_JOB_SEED_INCOMPLETE; data: { category: string } }
  | { errorType: typeof ContractorErrorType.PRACTICE_JOB_NOT_ON_PLATE; data: { category: string; projectId: number } }
  | {
      errorType:
        | typeof ContractorErrorType.TRAINING_SANDBOX_PROVISION_REJECTED
        | typeof ContractorErrorType.TRAINING_SANDBOX_PROVISION_FAILED;
      data: { contractorName: string; status: number };
    }
  | {
      errorType: typeof ContractorErrorType.PREFERRED_AUTO_ASSIGNMENT_INCOMPLETE;
      data: { taskId: string; issue: string };
    }
  | { errorType: typeof ContractorErrorType.FAILED_TO_CREATE_GOOGLE_CALENDAR; data: { calendarName: string } }
  | { errorType: typeof ContractorErrorType.REGIONS_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof ContractorErrorType.NO_PHONE_NUMBER_FOUND_FOR_HOMEOWNER_OR_CONTRACTOR;
      data: { projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.FAILED_TO_UPDATE_COUNTY_FROM_ADDRESS;
      data: { address: string; city: string; stateCode: string; zip: string };
    }
  | { errorType: typeof ContractorErrorType.PROPERTY_ADDRESS_NOT_FOUND; data: { addressId: string } }
  | { errorType: typeof ContractorErrorType.COULD_NOT_FIND_COUNTY_OR_LOCALITY_FOR_ADDRESS; data: { address: string } }
  | {
      errorType: typeof ContractorErrorType.MARKET_CREATION_FAILED;
      data: {
        oldMarket: string;
        newMarket: string;
        newMarketCounties: Array<{ name: string; stateCode: string }>;
        marketsToCopyContractorsFrom?: string[];
      };
    }
  | {
      errorType: typeof ContractorErrorType.MARKET_NOT_FOUND_FOR_SERVICE_AREA;
      data: { contractorProfileId: string; serviceAreaName?: string };
    }
  | { errorType: typeof ContractorErrorType.COUNTIES_NOT_FOUND; data: undefined }
  | { errorType: typeof ContractorErrorType.CONTRACTOR_PROFILE_NOT_FOUND; data: { contractorProfileId: string } }
  | {
      errorType: typeof ContractorErrorType.PROJECT_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.CANNOT_FILTER_CONTRACTORS_WITHOUT_MARKET_ID;
      data: { projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.CANNOT_FILTER_CONTRACTORS_WITHOUT_WORKSITE_GEO_LOCATION;
      data: { projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.FAILED_TO_GET_CONTRACTORS_WITH_MARGIN;
      data: { projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.BID_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.CPM_NOT_FOUND;
      data: { projectId: number; regionId: string; contractorId: string };
    }
  | {
      errorType: typeof ContractorErrorType.PROJECT_HAS_NO_BID;
      data: { projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.DUPLICATE_CONTRACTOR_NAME;
      data: { contractorName: string };
    }
  | {
      errorType: typeof ContractorErrorType.COMPLIANCE_REMINDER_INVALID_REQUEST;
      data:
        | { tag: "NO_CHANNELS" }
        | { tag: "NO_RECIPIENTS" }
        | { tag: "INVALID_RECIPIENTS"; personIds: string[] }
        | { tag: "NO_EMAIL_RECIPIENTS" }
        | { tag: "NO_PHONE_RECIPIENTS" }
        | { tag: "SHARED_SMS_LINE_NOT_CONFIGURED" }
        | { tag: "USER_OPEN_PHONE_NOT_ASSIGNED"; userPersonId: string }
        | { tag: "SMS_FROM_MODE_REQUIRED" }
        | { tag: "REMINDER_BLOCKED_DOCUMENTS_IN_REVIEW"; pendingCount: number };
    }
  | { errorType: typeof ContractorErrorType.CONTRACTOR_USER_PRIMARY_EMAIL_REQUIRED; data: undefined }
  | {
      errorType: typeof ContractorErrorType.DOCUMENT_REJECTION_INVALID_RECIPIENTS;
      data: { personIds: string[] };
    }
  | {
      errorType: typeof ContractorErrorType.SCORECARD_SNAPSHOT_NOT_FOUND;
      data: { contractorProfileId: string };
    }
  | {
      errorType: typeof ContractorErrorType.SCORECARD_SNAPSHOT_READBACK_FAILED;
      data: { contractorProfileId: string; snapshotId: string };
    }
  | {
      errorType: typeof ContractorErrorType.SCORECARD_UNKNOWN_METRIC_KEY;
      data: { metricKey: string };
    }
  | {
      errorType: typeof ContractorErrorType.SCORECARD_WEIGHT_OUT_OF_RANGE;
      data: { metricKey: string; weight: number };
    }
  | {
      errorType: typeof ContractorErrorType.SERVICE_AREA_NOT_FOUND;
      data: { serviceAreaId: string };
    }
  | {
      errorType: typeof ContractorErrorType.FAILED_TO_WRITE_SELECTION_HISTORY;
      data: {
        projectId: number;
        contractorProfileId: string;
      };
    }
  | {
      errorType: typeof ContractorErrorType.SURVEY_ONLY_CONTRACTOR_ON_NON_SURVEY_PROJECT;
      data: { projectId: number; contractorOrgId: string };
    }
  | {
      errorType: typeof ContractorErrorType.DOCUMENT_EXPIRATION_REMINDER_BATCH_FAILED;
      data: {
        totalContractors: number;
        failedCount: number;
        failedContractorProfileIds: string[];
      };
    }
  | { errorType: typeof ContractorErrorType.COMMITMENT_NOT_FOUND; data: { commitmentId: string } }
  | { errorType: typeof ContractorErrorType.COMMITMENT_EDIT_CONFLICT; data: { commitmentId: string } }
  | {
      errorType: typeof ContractorErrorType.PRE_ASSIGNMENT_NOT_FOUND;
      data: { preAssignmentId: string; bidId: number };
    }
  | {
      errorType: typeof ContractorErrorType.COMMITMENT_DELETE_UNAUTHORIZED;
      data: { commitmentId: string; contractorProfileId: string };
    }
  | { errorType: typeof ContractorErrorType.ASSIGNEE_NOT_REMI_INTERNAL; data: { personId: string } }
  | {
      errorType: typeof ContractorErrorType.ASSIGNEE_NOT_ACTIVE_SITE_INSPECTOR;
      data: { personId: string; contractorProfileId: string };
    }
  | { errorType: typeof ContractorErrorType.ASSIGNEE_PERSON_NOT_FOUND; data: { personId: string } }
  | { errorType: typeof ContractorErrorType.RECEIPT_NOT_FOUND; data: { receiptId: string } }
  | { errorType: typeof ContractorErrorType.EXPORT_USER_NOT_CONTRACTOR; data: { organizationId: string } }
  | { errorType: typeof ContractorErrorType.EXPORT_DISABLED_FOR_ORG; data: { organizationId: string } }
  | { errorType: typeof ContractorErrorType.EXPORT_RATE_LIMITED; data: { organizationId: string } }
  | {
      errorType: typeof ContractorErrorType.SITE_INSPECTOR_NOT_ACTIVE_IN_PROJECT_ORG;
      data: { projectId: number; siteInspectorId: string; contractorOrganizationIds: string[] };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_INSPECTOR_NOT_QUALIFIED_FOR_PROJECT_CATEGORY;
      data: { projectId: number; siteInspectorId: string; projectCategory: string; requiredJobTypeRole: string };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_INSPECTOR_HAS_UPCOMING_VISITS;
      data: { personId: string; upcomingDays: { dayKey: string; count: number }[] };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_INSPECTOR_INVALID_WORKING_HOURS;
      data: { personId: string; workingHoursStart: string | null; workingHoursEnd: string | null };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_INSPECTOR_INVALID_TIME_OFF;
      data: { personId: string; date: string; todayKey: string; blocks: SiteVisitBlock[] };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_INSPECTOR_NOT_IN_CONTRACTOR_ORG;
      data: { personId: string; contractorProfileId: string };
    }
  | {
      errorType: typeof ContractorErrorType.NOMAD_JOB_TYPE_ROLES_CONFER_CAPABILITIES;
      data: { personId: string; contractorProfileId: string; capabilities: string[] };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_VISIT_APPOINTMENT_CONFLICT;
      data: {
        projectId: number;
        existingAppointmentId: string;
        existingSiteInspectorId: string;
        requestedSiteInspectorId: string;
      };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_VISIT_APPOINTMENT_NOT_IN_CONTRACTOR_ORG;
      data: { appointmentId: string; organizationId: string };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_VISIT_APPOINTMENT_NOT_ON_INSPECTOR_ROUTE;
      data: { appointmentId: string };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_VISIT_APPOINTMENT_NOT_SCHEDULED;
      data: { appointmentId: string; status: string };
    }
  | {
      errorType: typeof ContractorErrorType.SITE_VISIT_ANOTHER_VISIT_EN_ROUTE;
      data: { appointmentId: string };
    }
  | {
      errorType: typeof ContractorErrorType.DISPATCH_REMOVAL_REFUSED;
      data: { projectId: number; reason: "notDispatchBoardCategory" | "contractorNotConfirmed" | "visitCompleted" };
    }
  | {
      errorType: typeof ContractorErrorType.INSPECTOR_SCHEDULE_SMS_LINE_NOT_CONFIGURED;
      data: { siteInspectorId: string };
    }
  | {
      errorType: typeof ContractorErrorType.CONTRACTOR_NOT_IN_ELIGIBLE_LIST;
      data: { contractorId: string; projectId: number };
    }
  | {
      errorType: typeof ContractorErrorType.CONTRACTOR_REASSIGNMENT_BLOCKED;
      data: { projectId: number; reviewTaskId: string; contractorId: string; blockers: string[] };
    }
  | {
      errorType: typeof ContractorErrorType.CONTRACTOR_ALREADY_ASSIGNED_TO_PROJECT;
      data: { projectId: number; contractorId: string; assignedOrganizationId: string };
    }
  | {
      errorType: typeof ContractorErrorType.PROJECTS_NOT_ON_SAME_BID;
      data: { projectIds: number[] };
    }
  | {
      errorType: typeof ContractorErrorType.CONTRACTOR_CAPABILITY_GROUPING_NOT_FOUND;
      data: { contractorCapabilityGroupingId: string };
    }
  | {
      errorType: typeof ContractorErrorType.CONTRACTOR_CAPABILITY_GROUPING_HAS_LINE_ITEMS;
      data: { contractorCapabilityGroupingId: string; lineItemCount: number };
    }
  | {
      errorType: typeof ContractorErrorType.CONTRACTOR_CAPABILITY_GROUPING_NAME_TAKEN;
      data: { name: string };
    }
  | {
      errorType: typeof ContractorErrorType.NOMAD_BOOKING_OWNER_COMPENSATION_FAILED;
      data: { projectId: number; appointmentId: string; siteInspectorId: string; cancelError: string };
    }
  | {
      errorType: typeof ContractorErrorType.NOMAD_ORG_HAS_NO_TAGGED_INSPECTORS;
      data: { projectId: number; contractorOrgId: string };
    }
  | {
      errorType: typeof ContractorErrorType.NOMAD_AUTO_ASSIGN_COMPENSATION_FAILED;
      data: {
        projectId: number;
        contractorOrgId: string;
        appointmentId: string | null;
        displacedContractorOrgId: string | null;
        failedSteps: string[];
      };
    };

export const contractorErrorDetails: Record<ContractorErrorType, ErrorDetails> = {
  EXPORT_USER_NOT_CONTRACTOR: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Contractor export requested by a non-contractor org",
  },
  EXPORT_DISABLED_FOR_ORG: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Contractor export requested while data export is disabled for the org",
  },
  EXPORT_RATE_LIMITED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Contractor export daily rate limit reached",
  },
  FAILED_TO_CREATE_GOOGLE_CALENDAR: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Failed to create Google Calendar",
  },
  REGIONS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Regions not found for project",
  },
  NO_PHONE_NUMBER_FOUND_FOR_HOMEOWNER_OR_CONTRACTOR: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No phone number found for homeowner or contractor",
  },
  FAILED_TO_UPDATE_COUNTY_FROM_ADDRESS: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to update county from address",
  },
  PROPERTY_ADDRESS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Property address not found",
  },
  COULD_NOT_FIND_COUNTY_OR_LOCALITY_FOR_ADDRESS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Could not find county or locality for address",
  },
  MARKET_CREATION_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to create new market",
  },
  MARKET_NOT_FOUND_FOR_SERVICE_AREA: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No market found for service area centroid",
  },
  COUNTIES_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Counties not found",
  },
  CONTRACTOR_PROFILE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor profile not found",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found",
  },
  CANNOT_FILTER_CONTRACTORS_WITHOUT_MARKET_ID: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Cannot filter contractors without a market name",
  },
  CANNOT_FILTER_CONTRACTORS_WITHOUT_WORKSITE_GEO_LOCATION: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Cannot filter contractors without a worksite geo location",
  },
  FAILED_TO_GET_CONTRACTORS_WITH_MARGIN: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Failed to get contractors with margin",
  },
  BID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid not found for project",
  },
  CPM_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "CPM not found for project",
  },
  PROJECT_HAS_NO_BID: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project has no bid",
  },
  DUPLICATE_CONTRACTOR_NAME: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A contractor with this name already exists",
  },
  COMPLIANCE_REMINDER_INVALID_REQUEST: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Contractor compliance reminder request failed validation",
  },
  CONTRACTOR_USER_PRIMARY_EMAIL_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Signed-in contractor user has no primary email for COI agent email CC",
  },
  DOCUMENT_REJECTION_INVALID_RECIPIENTS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Document rejection recipients do not belong to the contractor organization",
  },
  SCORECARD_SNAPSHOT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor has no scorecard snapshot to export",
  },
  SCORECARD_SNAPSHOT_READBACK_FAILED: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Contractor scorecard snapshot readback did not match the newly written id",
  },
  SCORECARD_UNKNOWN_METRIC_KEY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Scorecard weight edit referenced a metric key not in the active registry",
  },
  SCORECARD_WEIGHT_OUT_OF_RANGE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Scorecard weight must be in [0, 1]",
  },
  SERVICE_AREA_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Service area not found",
  },
  FAILED_TO_WRITE_SELECTION_HISTORY: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Failed to write contractor selection history row",
  },
  SURVEY_ONLY_CONTRACTOR_ON_NON_SURVEY_PROJECT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A site-survey-only contractor can only be assigned to site survey projects",
  },
  DOCUMENT_EXPIRATION_REMINDER_BATCH_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Document expiration reminder batch had failures",
  },
  COMMITMENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor assignment commitment not found",
  },
  COMMITMENT_EDIT_CONFLICT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Contractor assignment commitment changed under a concurrent edit",
  },
  COMMITMENT_DELETE_UNAUTHORIZED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Caller is not authorized to delete this contractor assignment commitment",
  },
  PRE_ASSIGNMENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor pre-assignment not found on the requested bid",
  },
  ASSIGNEE_NOT_REMI_INTERNAL: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Assignee is not a Remi internal user",
  },
  ASSIGNEE_NOT_ACTIVE_SITE_INSPECTOR: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Assignee is not an active site inspector in the service area's contractor org",
  },
  ASSIGNEE_PERSON_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Person not found for contractor-profile role assignment",
  },
  RECEIPT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Receipt not found",
  },
  // The board drops an inactive inspector's crew on every date, so removing one with live visits
  // would leave those visits booked but unreachable from the board.
  SITE_INSPECTOR_HAS_UPCOMING_VISITS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Refused to take a site inspector off dispatch while they still have upcoming visits",
  },
  SITE_INSPECTOR_INVALID_WORKING_HOURS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Refused a site inspector shift that is not a valid HH:MM range ending after it starts",
  },
  SITE_INSPECTOR_INVALID_TIME_OFF: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Refused site inspector time off on a day that has already passed",
  },
  SITE_INSPECTOR_NOT_ACTIVE_IN_PROJECT_ORG: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Site-visit appointment named an inspector outside the project's contractor org",
  },
  // The inspector is inside the project's org, so this is not a tenancy boundary — it is a roster not
  // signed off for this kind of visit. 400 rather than 403 because the dispatch save path swallows a
  // 403 as a benign denial, which would lose the refusal entirely.
  SITE_INSPECTOR_NOT_QUALIFIED_FOR_PROJECT_CATEGORY: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Site-visit appointment named an inspector without the visit's required job-type role",
  },
  // Not AUTHORIZATION_ERROR like the entry above: authorization is enforced by the procedure's
  // capability gate, so a mismatch reaching this check is incoherent input rather than a tenancy
  // violation. Pinned by RemiServiceError.contractor-classification.test.ts.
  // The caller asked to book a visit for a project that already holds a different active one that day.
  // Not AUTHORIZATION_ERROR: the caller is authorized, the board's view of the day is simply stale.
  SITE_VISIT_APPOINTMENT_CONFLICT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Site-visit create found a different active appointment for the project that day",
  },
  SITE_INSPECTOR_NOT_IN_CONTRACTOR_ORG: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Inspector config write named a person who is not a site inspector in that contractor org",
  },
  // Not AUTHORIZATION_ERROR: the procedure's object-level gate is the boundary, so an org that scopes the
  // write to zero rows is incoherent input. Never 403 — the dispatch save path swallows a 403 as benign.
  SITE_VISIT_APPOINTMENT_NOT_IN_CONTRACTOR_ORG: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Site-visit appointment write named a contractor org that does not own the appointment",
  },
  SITE_VISIT_APPOINTMENT_NOT_ON_INSPECTOR_ROUTE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription:
      "Inspector on-the-way or decline write named an appointment that is not on the signed-in inspector's route",
  },
  SITE_VISIT_APPOINTMENT_NOT_SCHEDULED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Inspector on-the-way or decline write named an appointment that is no longer scheduled or already started",
  },
  SITE_VISIT_ANOTHER_VISIT_EN_ROUTE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Inspector marked a visit on the way while another of that day's visits was already en route",
  },
  DISPATCH_REMOVAL_REFUSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Remove-from-dispatch named a project that is not a confirmed, unfinished dispatch-board visit",
  },
  INSPECTOR_SCHEDULE_SMS_LINE_NOT_CONFIGURED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Inspector schedule text could not send because the shared contractor SMS line is missing",
  },
  NOMAD_JOB_TYPE_ROLES_CONFER_CAPABILITIES: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Nomad job-type roles gained a capability, invalidating the weaker gate on their write path",
  },
  CONTRACTOR_NOT_IN_ELIGIBLE_LIST: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Contractor is not in the eligible assignment list for the project",
  },
  CONTRACTOR_REASSIGNMENT_BLOCKED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A work order rejection named a replacement contractor that can't take the job",
  },
  CONTRACTOR_ALREADY_ASSIGNED_TO_PROJECT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "A queued assignment found another contractor already assigned to the project",
  },
  PROJECTS_NOT_ON_SAME_BID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Bulk assign projects do not belong to the same bid",
  },
  [ContractorErrorType.CONTRACTOR_CAPABILITY_GROUPING_NOT_FOUND]: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor capability grouping not found",
  },
  [ContractorErrorType.CONTRACTOR_CAPABILITY_GROUPING_HAS_LINE_ITEMS]: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot delete a contractor capability grouping that has assigned line items",
  },
  [ContractorErrorType.CONTRACTOR_CAPABILITY_GROUPING_NAME_TAKEN]: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A contractor capability grouping with this name already exists",
  },
  // Distinct from the individual step failures it follows: those are ordinary transient errors, this is
  // the state they leave behind. The attach or booking that survived makes the next auto-complete read
  // the failed run as a success, so it needs reconciling before that retry, not after.
  [ContractorErrorType.NOMAD_AUTO_ASSIGN_COMPENSATION_FAILED]: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Nomad auto-assign could not roll back a failed assignment, leaving the project inconsistent",
  },
  // The booking holds the visit off the unscheduled pool yet never reaches the crew's device, so it
  // needs a dispatcher to cancel or re-own it before the visit's block passes.
  NOMAD_BOOKING_OWNER_COMPENSATION_FAILED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Nomad booked a visit, could not set its owner, and could not cancel the ownerless booking",
  },
  // tryToBook treats an org with no tagged inspectors as one Nomad doesn't govern and lets the
  // confirmation through unbooked — for Nomad itself that is a roster misconfiguration, not a pass.
  NOMAD_ORG_HAS_NO_TAGGED_INSPECTORS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Nomad has no person tagged as a site inspector, so a visit was confirmed without a booking",
  },
  PREFERRED_AUTO_ASSIGNMENT_INCOMPLETE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Preferred automatic assignment could not persist or resume its allocation",
  },
  SIMULATION_MARKET_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Preferred market simulation was asked for a market that does not exist",
  },
  TRAINING_SANDBOX_REQUIRED: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor training is only available in the training sandbox",
  },
  TRAINING_CONTRACTOR_NOT_ACTIVE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Only an active contractor can be provisioned for training",
  },
  TRAINING_SANDBOX_PROVISION_REJECTED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "The training sandbox refused a new contractor sent from production",
  },
  TRAINING_SANDBOX_PROVISION_FAILED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "The training sandbox could not take a new contractor sent from production",
  },
  PRACTICE_CATEGORY_NOT_APPROVED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A practice job was requested for a category the training contractor is not approved for",
  },
  PRACTICE_JOB_SEED_INCOMPLETE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A practice job seed finished without reaching its stop task",
  },
  PRACTICE_JOB_NOT_ON_PLATE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A seeded practice job did not land on the training contractor's plate",
  },
};
